import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon } from '@/lib/icons';

export type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  fullWidth?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-ink-900 text-paper hover:bg-ink-800 active:bg-ink-950 disabled:bg-ink-400 disabled:text-paper border border-transparent ' +
    // Nur die FESTEN Akzent-Töne (400/500) taugen als `dark:`-Override — 600
    // spiegelt und wäre im Dunkelmodus heller als der Ruhezustand.
    'dark:bg-accent-500 dark:hover:bg-accent-400 dark:active:bg-accent-400',
  secondary:
    'bg-paper text-ink-800 border border-ink-300 hover:bg-ink-50 hover:border-ink-400 disabled:bg-ink-100 disabled:text-ink-400',
  accent:
    'bg-accent-500 text-ink-950 hover:bg-accent-300 active:bg-accent-600 disabled:bg-accent-100 disabled:text-ink-400 border border-transparent font-medium',
  ghost:
    'bg-transparent text-ink-700 hover:bg-ink-100 disabled:text-ink-400 border border-transparent',
  danger:
    // `bg-danger` ist ein FESTES Rot — dort gehört `text-white`, nicht
    // `text-paper`: paper spiegelt mit und wäre im Dunkelmodus dunkelblau.
    'bg-danger text-white hover:bg-red-800 active:bg-red-900 disabled:bg-ink-300 border border-transparent',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-base gap-2',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    disabled,
    iconLeft,
    iconRight,
    fullWidth,
    className,
    type = 'button',
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      // Klick-Sound: globaler Listener in SoundProvider triggert auf
      // [data-soundclick]. Default OFF; im TopBar einschaltbar.
      data-soundclick=""
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded font-medium transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper dark:focus-visible:ring-offset-ink-950',
        // Subtile Hover-Lift für nicht-ghost Varianten (J2 Look & Feel).
        // Wir lassen das Element 1 px nach oben und werfen einen elev-1-Schatten.
        // active:translate-y-0 ist der Klick-Bounce.
        variant !== 'ghost' && !disabled && !loading
          ? 'hover:-translate-y-px hover:shadow-elev-1 active:translate-y-0'
          : '',
        'disabled:cursor-not-allowed',
        fullWidth && 'w-full',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <Icon name="Loader2" size={16} className="animate-spin" /> : iconLeft}
      {children}
      {iconRight}
    </button>
  );
});
